/** Answering a site's lease treatment - **ADR-0068 option C, QA F-032**.
 *
 * `create_facility` leaves the treatment at `unknown` on purpose, and every filing the site
 * contributes to is held until somebody states it. Until this hook existed nothing could: the only
 * escape was retiring the site and creating another, which orphans its meters.
 *
 * **A first answer only.** The route refuses a site that has already been answered
 * (`lease_already_answered`, 409), because the two columns are undated and overwriting one is a
 * restatement of every window the site has been in. That is ADR-0068 option B, which needs a table
 * and is not built; the screen says so rather than offering a control that predictably fails.
 *
 * **What an answer touches.** It changes which figures a filing *sums* (the boundary is applied at
 * read time), not any calculation, receipt or issued artifact. So the reads that go stale are the
 * site list and the reports - and, because a held filing may now be fit to file, the obligations
 * list and the artifacts' re-derivations. They are invalidated by prefix because this screen does
 * not know which obligation anybody is looking at.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type LeaseAnswerIn = components["schemas"]["LeaseAnswerIn"];
export type LeaseAnswer = components["schemas"]["LeaseAnswerOut"];

export function useAnswerLease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { facilityId: string } & LeaseAnswerIn): Promise<LeaseAnswer> => {
      const { facilityId, ...body } = input;
      const { data, error, response } = await api.PATCH("/api/v1/facilities/{facility_id}/lease", {
        params: { path: { facility_id: facilityId } },
        body,
      });
      // The server's sentence, not ours: every refusal on this route is written to be read, and
      // `lease_already_answered` names what was stated.
      if (error || !data) throw refusalFrom(response, error, "That answer could not be saved.");
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities"] });
      void queryClient.invalidateQueries({ queryKey: ["report"] });
      void queryClient.invalidateQueries({ queryKey: ["obligations"] });
      void queryClient.invalidateQueries({ queryKey: ["artifacts"] });
    },
  });
}
